import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Mail, Phone, ArrowLeft, RefreshCw, CheckCheck, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import {
  adminContactsList,
  adminContactMarkRead,
  adminContactsMarkAllRead,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHu = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const sourceLabel = (src) => {
  const map = {
    chat_widget: 'Chat widget',
    contact_section: 'Kapcsolat űrlap',
  };
  return map[src] || src || 'Kapcsolat űrlap';
};

const ContactCard = ({ contact, onMarkRead, marking }) => {
  const isNew = contact.status === 'new';
  return (
    <div
      data-testid={`admin-contact-${contact.id}`}
      className={`rounded-xl border p-5 transition-colors ${
        isNew
          ? 'border-red-500/40 bg-red-500/5 hover:border-red-500/70'
          : 'border-gray-800 bg-gray-900/60 hover:border-gray-700'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            isNew ? 'bg-red-500/20' : 'bg-gray-800'
          }`}>
            <MessageSquare size={16} className={isNew ? 'text-red-300' : 'text-gray-400'} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-bold text-white truncate">{contact.name}</div>
              {isNew && (
                <span
                  data-testid={`admin-contact-new-badge-${contact.id}`}
                  className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500 text-white"
                >
                  ÚJ
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {fmtHu(contact.created_at)} · {sourceLabel(contact.source)}
            </div>
          </div>
        </div>
        {isNew && (
          <Button
            data-testid={`admin-contact-mark-read-${contact.id}`}
            onClick={() => onMarkRead(contact.id)}
            disabled={marking}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
          >
            {marking ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <CheckCheck size={12} className="mr-1.5" />}
            Olvasottnak jelöl
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <a
          href={`mailto:${contact.email}?subject=${encodeURIComponent('Válasz: Orosz Coaching')}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-200"
        >
          <Mail size={12} /> {contact.email}
        </a>
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-200"
          >
            <Phone size={12} /> {contact.phone}
          </a>
        )}
        {!isNew && contact.read_at && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300">
            <Clock size={11} /> Olvasva: {fmtHu(contact.read_at)}
          </span>
        )}
      </div>

      <div className="rounded-lg bg-gray-950/60 border border-gray-800 p-3 text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
        {contact.message}
      </div>
    </div>
  );
};

const AdminContactsPage = () => {
  useDocumentMeta({ title: 'Kapcsolat · Admin | Orosz Coaching' });
  const [adminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | unread | read

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const res = await adminContactsList(adminKey);
      setRows(res.contacts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (id) => {
    setMarkingId(id);
    try {
      await adminContactMarkRead(adminKey, id);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'read', read_at: new Date().toISOString() } : r)));
      toast.success('Olvasottnak jelölve.');
    } catch (err) {
      toast.error('Hiba: ' + (err?.response?.data?.detail || err.message));
    } finally { setMarkingId(null); }
  };

  const handleMarkAllRead = async () => {
    if (!window.confirm('Minden új üzenetet olvasottnak jelölsz?')) return;
    setMarkingAll(true);
    try {
      const res = await adminContactsMarkAllRead(adminKey);
      toast.success(`${res.updated} üzenet olvasottnak jelölve.`);
      await load();
    } catch (err) {
      toast.error('Hiba: ' + (err?.response?.data?.detail || err.message));
    } finally { setMarkingAll(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'unread' && r.status !== 'new') return false;
      if (filter === 'read' && r.status === 'new') return false;
      if (!q) return true;
      return (
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.message || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query]);

  const unreadCount = useMemo(() => rows.filter((r) => r.status === 'new').length, [rows]);
  const totalCount = rows.length;

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
          <p className="text-gray-300 mb-4">Előbb jelentkezz be az admin dashboardon.</p>
          <Link to="/admin/dashboard">
            <Button className="bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold">
              <ArrowLeft size={14} className="mr-1.5" /> Admin Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-400 hover:text-white">
              <ArrowLeft size={20} />
            </Link>
            <div className="w-10 h-10 rounded-full bg-pink-500/15 flex items-center justify-center">
              <MessageSquare className="text-pink-400" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Kapcsolat üzenetek
                {unreadCount > 0 && (
                  <span
                    data-testid="admin-contacts-header-badge"
                    className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-500 text-white text-xs font-black align-middle"
                  >
                    {unreadCount}
                  </span>
                )}
              </h1>
              <p className="text-xs text-gray-500">Chat widget + kapcsolat űrlap beérkező üzenetek.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-contacts-refresh"
              variant="ghost" size="sm" onClick={load}
              className="text-gray-300 hover:text-white"
            >
              <RefreshCw size={14} className="mr-1.5" /> Frissítés
            </Button>
            {unreadCount > 0 && (
              <Button
                data-testid="admin-contacts-mark-all-read"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
              >
                {markingAll ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <CheckCheck size={12} className="mr-1.5" />}
                Mind olvasottnak jelöl
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="flex rounded-lg overflow-hidden border border-gray-800 bg-gray-900">
            {[
              { k: 'all', label: `Összes (${totalCount})` },
              { k: 'unread', label: `Új (${unreadCount})` },
              { k: 'read', label: `Olvasott (${totalCount - unreadCount})` },
            ].map((tab) => (
              <button
                key={tab.k}
                data-testid={`admin-contacts-filter-${tab.k}`}
                onClick={() => setFilter(tab.k)}
                className={`text-xs font-bold uppercase tracking-widest px-3 py-2 transition-colors ${
                  filter === tab.k
                    ? 'bg-yellow-500 text-gray-900'
                    : 'text-gray-400 hover:bg-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Input
            data-testid="admin-contacts-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Keresés név, email, telefon, üzenet…"
            className="flex-1 min-w-[220px] max-w-md bg-gray-900 border-gray-800 text-white"
          />
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-yellow-500 mb-2" size={28} /><p className="text-gray-400 text-sm">Betöltés…</p></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <MessageSquare size={40} className="mx-auto mb-3 text-gray-700" />
            <p className="text-sm">
              {query || filter !== 'all' ? 'Nincs a szűrésnek megfelelő üzenet.' : 'Egyelőre nem érkezett üzenet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                onMarkRead={handleMarkRead}
                marking={markingId === c.id}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminContactsPage;
